import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="timezone-formatter"
export default class extends Controller {
  static values = {
    datetime: String,
    format: String,
    mode: String,
    layout: String,
  }

  connect() {
    this.formatTime()
    this.startAutoRefresh()
  }

  disconnect() {
    this.stopAutoRefresh()
  }

  startAutoRefresh() {
    const mode = this.modeValue || "date"

    // Only start auto-refresh for modes that show relative time
    if (mode === "ago" || mode === "both") {
      this.scheduleNextRefresh()
    }
  }

  scheduleNextRefresh() {
    // Clear any existing timeout
    this.stopAutoRefresh()

    const date = new Date(this.datetimeValue)
    const now = new Date()
    const diffMs = now - date
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    let nextRefreshMs

    if (diffSeconds < 60) {
      // Currently showing "X seconds ago" or "just now"
      // Next change: when we hit the next second (for first 10 seconds) or next minute
      if (diffSeconds < 10) {
        nextRefreshMs = 1000 // Update every second for first 10 seconds
      } else {
        // Schedule for when we'll hit "1 minute ago"
        nextRefreshMs = (60 - diffSeconds) * 1000
      }
    } else if (diffMinutes < 60) {
      // Currently showing "X minutes ago"
      // Schedule for when we'll hit the next minute
      const secondsUntilNextMinute = 60 - (diffSeconds % 60)
      nextRefreshMs = secondsUntilNextMinute * 1000
    } else if (diffHours < 24) {
      // Currently showing "X hours ago"
      // Schedule for when we'll hit the next hour
      const secondsUntilNextHour =
        (60 - (diffMinutes % 60)) * 60 - (diffSeconds % 60)
      nextRefreshMs = secondsUntilNextHour * 1000
    } else if (diffDays < 30) {
      // Currently showing "X days ago"
      // Schedule for when we'll hit the next day
      const secondsUntilNextDay =
        (24 - (diffHours % 24)) * 3600 -
        (diffMinutes % 60) * 60 -
        (diffSeconds % 60)
      nextRefreshMs = secondsUntilNextDay * 1000
    } else {
      // For months/years, refresh once per day to check for threshold changes
      nextRefreshMs = 24 * 60 * 60 * 1000 // 24 hours
    }

    // Add a small buffer to ensure we've crossed the threshold
    nextRefreshMs += 100

    // Cap the refresh time to reasonable limits
    nextRefreshMs = Math.min(nextRefreshMs, 24 * 60 * 60 * 1000) // Max 24 hours
    nextRefreshMs = Math.max(nextRefreshMs, 1000) // Min 1 second

    this.refreshTimeout = setTimeout(() => {
      this.formatTime()
      // Schedule the next refresh
      this.scheduleNextRefresh()
    }, nextRefreshMs)
  }

  stopAutoRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout)
      this.refreshTimeout = null
    }
  }

  formatTime() {
    if (!this.datetimeValue) return

    try {
      const date = new Date(this.datetimeValue)

      if (isNaN(date.getTime())) {
        console.warn("Invalid datetime value:", this.datetimeValue)
        return
      }

      const format = this.formatValue || "long"
      const mode = this.modeValue || "date"
      const layout = this.layoutValue || "inline"

      if (mode === "ago" || mode === "both") {
        const useCompact = layout === "compact"
        const relativeTime = useCompact
          ? this.formatRelativeTimeCompact(date)
          : this.formatRelativeTime(date)

        if (mode === "ago") {
          this.element.textContent = relativeTime
        } else {
          // mode === "both" - create HTML structure with muted relative time
          let absoluteTime
          if (useCompact) {
            // Compact absolute: drop date if same as today, otherwise use short form
            const now = new Date()
            const sameDay =
              now.getFullYear() === date.getFullYear() &&
              now.getMonth() === date.getMonth() &&
              now.getDate() === date.getDate()
            if (sameDay) {
              absoluteTime = this.formatAbsoluteTime(date, "time-only-compact")
            } else {
              const sameYear = now.getFullYear() === date.getFullYear()
              absoluteTime = this.formatAbsoluteTime(
                date,
                sameYear ? "short-no-year" : "short",
              )
            }
          } else {
            absoluteTime = this.formatAbsoluteTime(date, format)
          }
          this.element.innerHTML = `${absoluteTime} <span class="text-muted">&middot; ${relativeTime}</span>`
        }
      } else {
        // mode === "date" (default)
        if (layout === "multiline") {
          const dateStr = this.formatAbsoluteTime(date, "date-only")
          const timeStr = this.formatAbsoluteTime(date, "time-only")
          this.element.innerHTML = `${dateStr}<br><span class="text-muted">${timeStr}</span>`
        } else {
          this.element.textContent = this.formatAbsoluteTime(date, format)
        }
      }

      // Add a title attribute with the full UTC time for reference
      this.element.title = `${date.toISOString()} (UTC)`
    } catch (error) {
      console.error("Error formatting datetime:", error)
    }
  }

  formatAbsoluteTime(date, format) {
    switch (format) {
      case "short":
        return this.normalizeMeridian(
          date.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }),
        )

      case "medium":
        return this.normalizeMeridian(
          date.toLocaleString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }),
        )

      case "long":
      default:
        return this.normalizeMeridian(
          date.toLocaleString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZoneName: "short",
          }),
        )

      case "time-only":
        return this.normalizeMeridian(
          date.toLocaleString(undefined, {
            hour: "numeric",
            minute: "2-digit",
            timeZoneName: "short",
          }),
        )

      case "time-only-compact":
        return this.normalizeMeridian(
          date.toLocaleString(undefined, {
            hour: "numeric",
            minute: "2-digit",
          }),
        )

      case "date-only":
        return date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })

      case "short-no-year":
        return this.normalizeMeridian(
          date.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }),
        )
    }
  }

  // Lowercase and remove preceding space for AM/PM markers
  normalizeMeridian(text) {
    if (typeof text !== "string") return text
    return text.replace(/\s?(AM|PM)\b/, (_m, mer) => mer.toLowerCase())
  }

  formatRelativeTime(date) {
    const now = new Date()
    const diffMs = now - date
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)
    const diffMonths = Math.floor(diffDays / 30)
    const diffYears = Math.floor(diffDays / 365)

    if (diffYears > 0) {
      return diffYears === 1 ? "1 year ago" : `${diffYears} years ago`
    } else if (diffMonths > 0) {
      return diffMonths === 1 ? "1 month ago" : `${diffMonths} months ago`
    } else if (diffDays > 0) {
      return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`
    } else if (diffHours > 0) {
      return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`
    } else if (diffMinutes > 0) {
      return diffMinutes === 1 ? "1 minute ago" : `${diffMinutes} minutes ago`
    } else {
      return "just now"
    }
  }

  formatRelativeTimeCompact(date) {
    const now = new Date()
    const diffMs = now - date
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)
    const diffMonths = Math.floor(diffDays / 30)
    const diffYears = Math.floor(diffDays / 365)

    if (diffYears > 0) return `${diffYears}y`
    if (diffMonths > 0) return `${diffMonths}mo`
    if (diffDays > 0) return `${diffDays}d`
    if (diffHours > 0) return `${diffHours}h`
    if (diffMinutes > 0) return `${diffMinutes}m`
    return "now"
  }
}



